import { CommonModule } from '@angular/common';
import { Component, computed, input } from '@angular/core';

import { DateTime } from 'luxon';

import { capitalize } from '../../helpers/text';
import { REPEAT_CYCLE } from '../../types/enums/jobs.enum';
import { RepeatCycleTypes } from '../../types/jobs';

@Component({
    selector: 'lib-repeat-cycle-text',
    imports: [CommonModule],
    templateUrl: './repeat-cycle-text.component.html',
    styleUrl: './repeat-cycle-text.component.css'
})
export class RepeatCycleTextComponent {
    repeatCycle = input.required<RepeatCycleTypes | string | null>();
    repeatDays = input<string[] | null>();
    repeatEndDate = input<string | null | undefined>();
    repeatFrequency = input<number | null | undefined>();
    repeatType = input<string | null>();
    shiftStartDate = input<string>();

    shiftDayOfWeek = computed(() => {
        const startDate = this.shiftStartDate();
        if (startDate) {
            return DateTime.fromISO(startDate).toFormat('EEEE');
        } else {
            return '';
        }
    });

    repeatText = computed(() => {
        const repeatCycle = this.repeatCycleText();
        const repeatEndDate = this.repeatEndDate();
        const repeatEnd = repeatEndDate ? this.formatShortDate(repeatEndDate) : undefined;

        const result: string[] = [];

        if (repeatCycle) {
            result.push(`${repeatCycle}`);
        }

        if (repeatEnd) {
            result.push(`until ${repeatEnd}`);
        }

        return result.join(', ');
    });

    /**
     * Format the repeat cycle text
     * @returns The formatted repeat cycle text
     */
    repeatCycleText = () => {
        return this.repeatCycle() === REPEAT_CYCLE.CUSTOM
            ? this.formatCustomRepeatCycle()
            : this.formatStandardRepeatCycle();
    };

    /**
     * Format a date as 'd MMM yyyy'
     * @param date The date to format
     * @returns The formatted date string
     */
    formatShortDate(date: string): string {
        return DateTime.fromISO(date).toFormat('d MMM yyyy');
    }

    /**
     * Format the repeat days as a comma-separated list
     * @returns The formatted repeat days string
     */
    private formatRepeatDays = computed(() => {
        return (
            this.repeatDays()
                ?.map(day => capitalize(day))
                .join(', ') || ''
        );
    });

    /**
     * Format the standard repeat cycle text (weekly, fortnightly, monthly)
     * @returns The formatted standard repeat cycle text
     */
    private formatStandardRepeatCycle = () => {
        switch (this.repeatCycle()) {
            case REPEAT_CYCLE.WEEKLY:
                return `Every week`;
            case REPEAT_CYCLE.FORTNIGHTLY:
                return `Every fortnight`;
            case REPEAT_CYCLE.MONTHLY:
                return `Every month`;
            default:
                return '';
        }
    };

    /**
     * Format the custom repeat cycle text
     * @returns The formatted custom repeat cycle text
     */
    private formatCustomRepeatCycle = () => {
        const amount = this.repeatFrequency() === 1 || !this.repeatFrequency() ? '' : this.repeatFrequency();
        const type = this.repeatType() || '';
        const plural = this.repeatFrequency() === 1 || !this.repeatFrequency() ? '' : 's';

        if (this.repeatDays()?.length) {
            return `Every ${this.shiftDayOfWeek() ? this.shiftDayOfWeek() : this.formatRepeatDays()}`;
        } else {
            return `Every ${amount} ${type}${plural}`;
        }
    };
}
